import { Modal, Spinner } from 'react-bootstrap'
import React, { useEffect, useState } from 'react'
import { useTheme } from '../../context/ThemeContext'
import { deletePageFileAttachment, updatePageById } from '../../services/pages'
import { awsFileUploadConfig } from '../../config/awsFileUpload'
import ReactS3Client from 'react-aws-s3-typescript'
import { FileUploadPage } from '../FileUploader/FileUpload'
import { useDispatch } from 'react-redux'
import { editPageInLoggedInUser, setSelectedPage, updateHomePages } from '../../store/slice/page-slice'

type Props = {
  attachmentModal: boolean
  setAttachmentModal: (value: boolean) => void
  page: any
  setPages?: (pages: any) => void
  pages?: any[]
  boardName?: string
  activeTab?: string
}

const PageAttachmentModal = ({
  attachmentModal,
  setAttachmentModal,
  page,
  setPages,
  pages,
  boardName,
  activeTab
}: Props): React.JSX.Element => {
  const { theme } = useTheme()
  const dispatch = useDispatch()

  const [selectedFile, setSelectedFile] = useState<any>([])
  const [fileErrorMsg, setFileErrorMsg] = useState('')
  const [saving, setSaving] = useState(false)

  const s3 = new ReactS3Client({
    ...awsFileUploadConfig,
    dirName: `pages-attachments/${process.env.REACT_APP_ENVIRONMENT}`,
  })

  useEffect(() => {
    const urlLinks: any = []
    if (page?.file_attachments) {
      for (const file of page?.file_attachments) {
        for (const [key, value] of Object.entries(file)) {
          const getFileName = (value as string)
            ?.toLowerCase()
            ?.split(`${process.env.REACT_APP_ENVIRONMENT}/`)[1]
          urlLinks.push({ key, value: value, showName: getFileName })
        }
      }
      setSelectedFile(urlLinks)
    }
    return () => setSelectedFile([])
    //eslint-disable-next-line
  }, [page?.id])

  const selectFileHandler = (fileList: any) => {
    const files = Array.from(fileList)
    const validFiles = files.filter(
      (file: any) =>
        !file.name.toLowerCase().endsWith('.html') &&
        !file.name.toLowerCase().endsWith('.mhtml')
    )
    const invalidFilesExt = files.filter(
      (file: any) =>
        file.name.toLowerCase().endsWith('.html') ||
        file.name.toLowerCase().endsWith('.mhtml')
    )
    if (invalidFilesExt.length > 0) {
      setFileErrorMsg('Does not support this extension')
    }
    setSelectedFile((prevFiles: any) => [...prevFiles, ...validFiles])
  }

  const deleteFileHandler = async (name: string, key: string, value: string) => {
    let files: any
    if (key) {
      files = selectedFile.filter((item: any) => item.key !== key)
    } else {
      files = selectedFile.filter((item: any) => item.name !== name)
    }
    try {
      const fileNameForDelete = value || name
      await s3.deleteFile(fileNameForDelete)
      if (key) {
        await deletePageFileAttachment(page?.id, key)
        // Update parent pages state to reflect deletion
        const updatedPage = {
          ...page,
          file_attachments: page?.file_attachments?.filter((file: any) => !file[key])
        }
        if (setPages && pages) {
          const filteredPages = pages.filter((p: any) => p.id !== page.id)
          setPages([updatedPage, ...filteredPages])
        }
        // Dispatch Redux actions to update UI
        dispatch(editPageInLoggedInUser(updatedPage))
        if (activeTab) {
          dispatch(updateHomePages({ id: activeTab, updatedPage: updatedPage }))
        }
        dispatch(setSelectedPage(updatedPage))
      }
      setSelectedFile(files)
    } catch (error) {
      return error
    }
  }

  const handleUploadFiles = async () => {
    const urls = []
    for (let index = 0; index < selectedFile.length; index++) {
      const filename = selectedFile[index].name
      if (filename) {
        const filenameUpdated = filename.substring(0, filename.lastIndexOf('.'))
        try {
          const uploadFile: any = await s3.uploadFile(selectedFile[index], filenameUpdated)
          urls.push(uploadFile.location)
        } catch (error) {
          return error
        }
      }
    }
    return urls
  }

  const handleSaveAttachments = async () => {
    setSaving(true)
    try {
      const fileLinks = await handleUploadFiles()

      if (fileLinks && Array.isArray(fileLinks) && fileLinks.length > 0) {
        const formData = new FormData()

        formData.append('title', page?.title)
        formData.append('boardId', page?.boardId)
        formData.append('status', page?.status)
        if (boardName) {
          formData.append('boardName', boardName)
        }
        if (page?.pageNumber) {
          formData.append('pageNumber', page?.pageNumber)
        }

        fileLinks.forEach((link: string) => formData.append('new_file_attachments[]', link))

        const res = await updatePageById(page?.id, formData)
        const updatedPage: any = res.data.data.page

        if (setPages && pages) {
          const filteredPages = pages.filter((p: any) => p.id !== page.id)
          setPages([updatedPage, ...filteredPages])
        }

        // Dispatch Redux actions to update UI (same as AddPageNote)
        dispatch(editPageInLoggedInUser(updatedPage))
        if (activeTab) {
          dispatch(updateHomePages({ id: activeTab, updatedPage: updatedPage }))
        }
        if (page?.id === updatedPage.id) {
          dispatch(setSelectedPage(updatedPage))
        }
      }

      setAttachmentModal(false)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving attachments:', error)
    } finally {
      setSaving(false)
    }
  }

  const hasNewFiles = selectedFile.some((file: any) => file.name && !file.key)

  return (
    <Modal
      contentClassName={theme === 'dark' ? 'theme__div' : ''}
      show={attachmentModal}
      onHide={() => setAttachmentModal(false)}
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      backdrop='static'
    >
      <Modal.Header>
        <Modal.Title
          className={`text-center w-100 fw-bold ${theme === 'dark' ? 'white__text' : ''}`}
        >
          Attachments for {page?.key || page?.title}
        </Modal.Title>
        <i
          className={`bi bi-x-lg modal-icon-custom ${theme === 'dark' && 'close'}`}
          onClick={() => setAttachmentModal(false)}
        ></i>
      </Modal.Header>
      <Modal.Body className={theme === 'dark' ? 'dark-mode-placeholder' : ''}>
        <FileUploadPage
          deleteFileHandler={deleteFileHandler}
          selectedFile={selectedFile}
          changeHandler={selectFileHandler}
        />
        {fileErrorMsg && (
          <div className='alert alert-warning mt-2'>{fileErrorMsg}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          className='btn btn-secondary'
          onClick={() => setAttachmentModal(false)}
        >
          Close
        </button>
        {hasNewFiles && (
          <button
            className='btn btn-primary'
            onClick={handleSaveAttachments}
            disabled={saving}
          >
            {saving ? <Spinner animation='border' size='sm' /> : 'Save Attachments'}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default PageAttachmentModal
